import React from "react";

import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { patchSaleLot, SaleLotAction } from "actions";

import { addSaleLot } from "actions/offline";

import { SplitLotModal } from "components/SplitLotModal";

import {
  KEYWORD_BUSINESS_NAME_NCV,
  KEYWORD_BUSINESS_NAME_TRANSIT,
  KEYWORD_CATEGORY_CRIPPLE,
  KEYWORD_CATEGORY_NCV,
  KEYWORD_CATEGORY_TRANSIT,
  SplitType,
} from "constants/saleLots";

import { calculateSplitPricingData, removeNulls } from "lib";

import { findBusinessWithName } from "lib/businesses";
import { getDefaultPropertyId } from "lib/properties";
import { doesSaleLotHaveWeight, getSplitComment } from "lib/saleLot";
import { attributeSpeciesFilter } from "lib/speciesAttributes";

import {
  selectPropertyEnrichedBusinessByBusinessIdLookup,
  getCategories,
  getSingleWeighWeightBySaleLotId,
  getCurrentSaleyardId,
  getSaleLotById,
  getSaleById,
} from "selectors";

// Helper function to find a category approximately matching a value
function findCategorySimilar(value) {
  return category => category.name.toLowerCase().indexOf(value) > -1;
}

// Returns the required split defaults for a cripple type split
function getCrippleValues(saleLot, businesses, speciesCategories) {
  const crippleCategory = speciesCategories.find(
    findCategorySimilar(KEYWORD_CATEGORY_CRIPPLE),
  );
  return {
    categoryId: crippleCategory && crippleCategory.id,
  };
}

// Returns the required split defaults for an ncv type split
function getNCVValues(saleLot, businesses, speciesCategories) {
  const ncvBuyer = Object.values(businesses).find(
    findBusinessWithName(KEYWORD_BUSINESS_NAME_NCV),
  );
  const ncvCategory = speciesCategories.find(
    findCategorySimilar(KEYWORD_CATEGORY_NCV),
  );
  return {
    buyerId: ncvBuyer && ncvBuyer.id,
    categoryId: ncvCategory && ncvCategory.id,
    totalPriceCents: 0,
  };
}

// Returns the required split defaults for a transit event split
function getTransitValues(saleLot, businesses, speciesCategories) {
  const transitBuyer = Object.values(businesses).find(
    findBusinessWithName(KEYWORD_BUSINESS_NAME_TRANSIT),
  );
  const transitCategory = speciesCategories.find(
    findCategorySimilar(KEYWORD_CATEGORY_TRANSIT),
  );
  return {
    buyerId: transitBuyer && transitBuyer.id,
    categoryId: transitCategory && transitCategory.id,
  };
}

const SplitLotModalContainer = ({
  saleLotId,
  splitType,
  fullScreen,
  returnTo,
}) => {
  const dispatch = useDispatch();

  const saleLot = useSelector(getSaleLotById(saleLotId));
  const saleId = saleLot?.livestocksale_id;
  const sale = useSelector(state =>
    saleId ? getSaleById(saleId)(state) : null,
  );
  const saleyardId = useSelector(getCurrentSaleyardId);
  const shouldMoveWeights = useSelector(
    state => getSingleWeighWeightBySaleLotId(saleLotId)(state) === 0,
  );
  const businesses = useSelector(
    selectPropertyEnrichedBusinessByBusinessIdLookup,
  );
  const allCategories = useSelector(getCategories);

  if (!saleLot || !sale) {
    return null;
  }

  const speciesId = sale.species_id;
  const categoriesList = Object.values(allCategories);
  const speciesCategories = categoriesList.filter(
    attributeSpeciesFilter(speciesId),
  );

  let splitDefaults = {};
  switch (splitType) {
    case SplitType.CRIPPLE:
      splitDefaults = getCrippleValues(saleLot, businesses, speciesCategories);
      break;
    case SplitType.NCV:
      splitDefaults = getNCVValues(saleLot, businesses, speciesCategories);
      break;
    case SplitType.TRANSIT:
      splitDefaults = getTransitValues(saleLot, businesses, speciesCategories);
      break;
    default:
      break;
  }

  const existingHead = saleLot.quantity;
  const buyerId = splitDefaults.buyerId || saleLot.buyer_id;
  const categoryId =
    splitDefaults.categoryId || saleLot.category_id || undefined;
  const totalPriceCents =
    typeof splitDefaults.totalPriceCents === "number"
      ? splitDefaults.totalPriceCents
      : null;
  const buyer = businesses[buyerId];

  function splitSaleLot(splitCount, newLotData = {}) {
    // Now the total price and weight with the reduced head count
    const pricingData = calculateSplitPricingData(
      saleLot,
      splitCount,
      shouldMoveWeights,
    );
    const patch = pricingData.originalLot;
    const cloned = removeNulls(saleLot);
    const newSaleLotId = uuidv4();
    const destinationPropertyId = getDefaultPropertyId(
      buyer,
      saleyardId,
      saleLot.buyer_way?.name || null,
    );
    const buyerIdForSplit = buyer?.id;
    const splitLot = {
      ...cloned,
      buyer_id: buyerIdForSplit,
      category_id: categoryId,
      id: undefined,
      lot_number: undefined,
      destination_property_id:
        // The backend erroneously returns a 400 error when the buyer is null and
        // the destination property is present (even when null)
        buyerIdForSplit ? destinationPropertyId : undefined,
      ...pricingData.splitLot,
      // If we are doing a predefined NCV split or similar (Cripple),
      // use the pre-defined price.
      total_price_cents:
        typeof totalPriceCents === "number"
          ? totalPriceCents
          : pricingData.splitLot.total_price_cents,
      quantity_tags_used: 0,
      ...newLotData,
    };
    dispatch(
      patchSaleLot({ id: saleLot.id, ...patch }, { changeReason: "Split" }),
    );
    dispatch(addSaleLot(splitLot, newSaleLotId));
    dispatch(
      SaleLotAction.comment(newSaleLotId, {
        comment: getSplitComment(saleLot, splitCount, saleLot.auctionPen),
        resolvedDate: new Date(),
      }),
    );
  }

  return (
    <SplitLotModal
      returnTo={returnTo}
      existingHead={existingHead}
      splitSaleLot={splitSaleLot}
      fullScreen={fullScreen}
      splitType={splitType}
      shouldMoveWeights={shouldMoveWeights}
      doesLotHaveWeight={doesSaleLotHaveWeight(saleLot)}
      saleLot={saleLot}
    />
  );
};

export default SplitLotModalContainer;
